import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { API, imgUrl } from "../../data";

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-warm px-4 py-3 text-sm font-semibold text-ink placeholder-mute/70 transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/25";

const EMPTY = { name: "", location: "", guests: "2", rooms: "1", price: "", images: [], external_booking_url: "", cin: "", active: true, order: 0 };

const FIELDS = [
  ["name", "Nome (es. Villa Petra)"],
  ["location", "Località"],
  ["guests", "Ospiti"],
  ["rooms", "Camere"],
  ["price", "Prezzo da (es. €120) — lascia vuoto per non mostrarlo"],
  ["external_booking_url", "Link gestionale Kross (opzionale)"],
  ["cin", "CIN / Codice identificativo (opzionale)"],
];

export default function PropertiesManager({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const idOf = (x) => x.id || x._id;

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/properties`, { headers });
    setItems(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        external_booking_url: form.external_booking_url?.trim() || null,
        price: form.price?.trim() || null,
        cin: form.cin?.trim() || null,
      };
      if (idOf(form)) {
        await axios.put(`${API}/admin/properties/${idOf(form)}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/properties`, { ...payload, order: items.length }, { headers });
      }
      toast.success("Casa salvata");
      setForm(null);
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa casa?")) return;
    await axios.delete(`${API}/admin/properties/${id}`, { headers });
    toast.success("Casa eliminata");
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Foto caricata");
    } catch {
      toast.error("Caricamento fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="properties-manager">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-mute">Le case attive appaiono nella sezione "Case selezionate" della home. La prima foto è la copertina.</p>
        <button data-testid="prop-add-button" onClick={() => setForm({ ...EMPTY })} className="btn-primary !px-5 !py-3 text-[13px]">
          <Plus className="h-4 w-4" />
          Nuova casa
        </button>
      </div>

      <div className="mt-6 grid gap-4">
        {items.map((item) => (
          <div key={idOf(item)} data-testid={`prop-row-${idOf(item)}`} className="flex flex-wrap items-center gap-5 rounded-2xl bg-white p-5 ring-1 ring-navy/10">
            <img src={imgUrl(item.images[0])} alt="" className="h-16 w-14 rounded-xl object-cover ring-1 ring-navy/10" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg font-semibold text-navy">{item.name}</p>
              <p className="text-xs font-semibold text-mute">
                {item.location} · {item.guests} ospiti · da {item.price}/notte · {item.images.length} foto · {item.active ? "online" : "bozza"}
              </p>
            </div>
            <button data-testid={`prop-edit-${idOf(item)}`} onClick={() => setForm({ ...item })} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold text-navy ring-1 ring-navy/15 transition-colors hover:bg-navy hover:text-white">
              <Pencil className="h-3.5 w-3.5" />
              Modifica
            </button>
            <button data-testid={`prop-delete-${idOf(item)}`} onClick={() => remove(idOf(item))} aria-label="Elimina" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-mute ring-1 ring-navy/15 transition-colors hover:bg-red-50 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {form && (
        <form data-testid="prop-form" onSubmit={save} className="mt-8 grid gap-5 rounded-[1.75rem] bg-sand p-7 ring-1 ring-navy/10 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-center justify-between sm:col-span-2 lg:col-span-3">
            <h3 className="font-display text-xl font-semibold text-navy">{idOf(form) ? "Modifica casa" : "Nuova casa"}</h3>
            <button type="button" onClick={() => setForm(null)} aria-label="Chiudi" className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-navy ring-1 ring-navy/15">
              <X className="h-4 w-4" />
            </button>
          </div>
          {FIELDS.map(([key, label]) => (
            <label key={key} className="grid gap-2">
              <span className="text-xs font-extrabold uppercase tracking-[0.12em] text-navy">{label}</span>
              <input data-testid={`prop-field-${key}`} value={form[key] ?? ""} onChange={set(key)} required={key === "name" || key === "location" || key === "guests"} className={inputCls} />
            </label>
          ))}

          <div className="sm:col-span-2 lg:col-span-3">
            <span className="text-xs font-extrabold uppercase tracking-[0.12em] text-navy">Foto (la prima è la copertina)</span>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {form.images.map((img, i) => (
                <span key={img} className="relative">
                  <img src={imgUrl(img)} alt="" className="h-24 w-20 rounded-xl object-cover ring-1 ring-navy/10" />
                  <button
                    type="button"
                    data-testid={`prop-image-remove-${i}`}
                    onClick={() => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== img) }))}
                    aria-label="Rimuovi foto"
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-white shadow"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                data-testid="prop-upload-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex h-24 w-20 flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-navy/20 text-mute transition-colors hover:border-sky hover:text-navy disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                <span className="text-[10px] font-extrabold uppercase tracking-wider">{uploading ? "Carico…" : "Aggiungi"}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="prop-upload-input" />
            </div>
          </div>

          <label className="flex items-center gap-3 sm:col-span-2 lg:col-span-3">
            <input data-testid="prop-field-active" type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-5 w-5 accent-navy" />
            <span className="text-sm font-bold text-navy">Visibile sul sito</span>
          </label>

          <button data-testid="prop-save-button" type="submit" disabled={saving} className="btn-primary sm:col-span-2 lg:col-span-3">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Salvataggio…" : "Salva casa"}
          </button>
        </form>
      )}
    </div>
  );
}
